import React, { MouseEvent, useEffect, useState } from 'react';
import Modal from '../../../components/modals/Modal';
import {
  Account,
  AccountCategory,
  AccountType,
  CompensationModel,
} from '../../../types/userTypes';
import { Loader } from '../../../components/Loader/Loader';
import './EditAccountModal.scss';
import {
  getMessageForWrongOrgNumber,
  isOrganisationNumberValid,
} from '../../../utils/country';
import Fetch from '../../../utils/fetch';
import { settings } from '../../../settings';
import Swal from 'sweetalert2';
import { capitalize } from '../../../utils/strings';
import RegEx from '../../new-order/regEx';
import { EditAccountForm } from './EditAccountForm';

interface ErrorMsg {
  message: string;
}

type AccountFields =
  | 'email'
  | 'name'
  | 'companyNumber'
  | 'phoneCode'
  | 'phoneNumber';
type ErrorMessages = {
  [field in AccountFields]?: ErrorMsg;
};

interface UpdateResult {
  success: boolean;
  errors: ErrorMsg[];
}

interface UpdateAccountRequest {
  accountId: number;
  email: string;
  name: string;
  companyNumber: string;
  countryCode: string;
  phoneCode: string;
  phoneNumber: string;
  paymentByInvoiceAllowed: boolean;
  compensationModel: CompensationModel;
}

const spaceOrDigits = /^(?=.*\d)[\d ]+$/;

export function EditAccountModal(props: {
  onCloseModal: (updated: boolean) => void;
  account: Account;
}) {
  const [account, setAccount] = useState<Account>(props.account);
  const [accountUpdated, setAccountUpdated] = useState<boolean>(false);
  const [updatingAccount, setUpdatingAccount] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<ErrorMessages>({});
  const [errors, setErrors] = useState<ErrorMsg[]>([]);

  useEffect(() => {
    const escFunction = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        props.onCloseModal(false);
      }
    };

    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [props]);

  const validateFields = (account: Account): ErrorMessages => {
    const fieldErrors: ErrorMessages = {};
    fieldErrors.name = validateAccountName(account) || undefined;
    fieldErrors.companyNumber =
      validateAccountCompanyNumber(account) || undefined;
    fieldErrors.phoneCode = validatePhoneCode(account) || undefined;
    fieldErrors.phoneNumber = validatePhoneNumber(account) || undefined;
    fieldErrors.email = validateEmail(account) || undefined;
    return fieldErrors;
  };

  const hasErrors = (fieldErrors: ErrorMessages): boolean => {
    return Object.values(fieldErrors).filter(err => !!err).length > 0;
  };

  const isValidOrganizationNumber = (companyNumber: string) => {
    const valid = spaceOrDigits.test(companyNumber);
    if (!valid) {
      setFieldErrors({
        ...fieldErrors,
        companyNumber: {
          message: 'Invalid organization number',
        },
      });
    }

    return valid;
  };

  const updateAccount = async (
    updateAccount: UpdateAccountRequest
  ): Promise<UpdateResult> => {
    try {
      const res = await Fetch.put({
        url: `${settings.newApiUrl}/account`,
        body: updateAccount,
      });
      if (res.ok) {
        return { success: true, errors: [] };
      } else if (res.status === 422) {
        return {
          success: false,
          errors: (await res.json()).map((msg: { message: string }) => ({
            message: msg.message,
          })),
        };
      } else {
        return {
          success: false,
          errors: [
            {
              message: 'Something went wrong. The account could not be updated',
            },
          ],
        };
      }
    } catch (e) {
      return {
        success: false,
        errors: [
          {
            message: 'Something went wrong. The account could not be updated',
          },
        ],
      };
    }
  };

  const saveAccount = async (e: MouseEvent) => {
    e.preventDefault();
    const fieldErrors = validateFields(account);
    setFieldErrors(fieldErrors);

    if (hasErrors(fieldErrors)) {
      return;
    }

    const confirm = await Swal.fire({
      title: 'Confirm account update',
      text: `Are you sure you want to update account '${props.account.name}' ?`,
      icon: 'warning',
      confirmButtonText: 'Yes',
      showCancelButton: true,
      focusCancel: true,
    });
    if (!confirm.value) {
      return;
    }

    setAccountUpdated(false);
    setErrors([]);
    setUpdatingAccount(true);

    const result = await updateAccount({
      accountId: account.id,
      email: account.email,
      name: account.name,
      companyNumber: account.companyNumber,
      countryCode: account.countryCode,
      phoneCode: account.phoneCode,
      phoneNumber: account.phoneNumber,
      paymentByInvoiceAllowed: account.paymentByInvoiceAllowed,
      compensationModel: account.compensationModel,
    });

    if (result.success) {
      setAccountUpdated(true);
      setErrors([]);
      setUpdatingAccount(false);
      setTimeout(() => props.onCloseModal(true), 1000);
    } else {
      setAccountUpdated(false);
      setErrors(result.errors);
      setUpdatingAccount(false);
    }
  };

  const syncAccount = (newValue: Partial<AccountType>) => {
    const newAccount = { ...account, ...newValue };
    setAccount(newAccount);
  };

  const sendApiCredentalMail = async (accountId: number): Promise<boolean> => {
    try {
      const res = await Fetch.put({
        url: `${settings.newApiUrl}/account/${accountId}/send-api-credentials`,
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  };

  const getApiCredentials = async (e: MouseEvent) => {
    e.preventDefault();
    const confirm = await Swal.fire({
      title: 'Confirm Sending API Credential Mail',
      text: "This will send an email containing API key and public token for the account to your user's registered email address. Are you sure you want to continue?",
      icon: 'warning',
      confirmButtonText: 'Yes',
      showCancelButton: true,
      focusCancel: true,
    });
    if (!confirm.value) {
      return;
    }
    const result = await sendApiCredentalMail(account.id);
    if (result === false) {
      await Swal.fire({
        title: 'Could not send API Credential email',
        text: 'An error occurred when trying to send the API credential email',
        icon: 'error',
      });
    }
  };

  return (
    <Modal
      title="Edit account"
      onClose={() => props.onCloseModal(accountUpdated)}
      isLoading={false}
      width={600}
      buttons={[
        {
          title: 'Get API credentials',
          disabled: account.category !== AccountCategory.SENDER,
          onClick: (e: MouseEvent) => {
            getApiCredentials(e);
          },
        },
        {
          title: 'Save',
          disabled: updatingAccount,
          onClick: (e: MouseEvent) => {
            saveAccount(e);
          },
        },
      ]}
    >
      <div className="edit-account-modal">
        <form className="edit-account-modal__form" autoComplete="off">
          <div className="edit-account-modal__form-group">
            <label>
              <span className="edit-account-modal__form-label">
                Account type
              </span>
              <div className="edit-account-modal__form-value">
                {capitalize(account.category)}
              </div>
            </label>
          </div>

          <EditAccountForm
            account={account}
            fieldErrors={fieldErrors}
            isValidOrganizationNumber={isValidOrganizationNumber}
            syncAccount={syncAccount}
          />

          <div className="edit-account-modal__form-submit">
            {updatingAccount && (
              <div className="edit-account-modal__form-loader">
                <Loader type="bounce" size="medium" />
              </div>
            )}
          </div>
          {errors.length > 0 && (
            <div className="u-alert__error">
              {errors.map((error: ErrorMsg, i: number) => {
                return (
                  <div key={`${error}-${i}`}>
                    <div style={{ fontWeight: 500 }}>{error.message}</div>
                  </div>
                );
              })}
            </div>
          )}
          {accountUpdated && (
            <div className="u-alert__info">
              <p>The account has been updated successfully.</p>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
}

function validateAccountName(account: Account): ErrorMsg | null {
  if (account.name.trim().length < 1) {
    return { message: 'Please enter the account name' };
  }
  return null;
}

function validateAccountCompanyNumber(account: Account): ErrorMsg | null {
  const companyNumber = account.companyNumber.trim();
  if (companyNumber.length < 1) {
    return { message: 'Please enter the organization number' };
  }
  if (!isOrganisationNumberValid(companyNumber, account.countryCode)) {
    return { message: getMessageForWrongOrgNumber(account.countryCode) };
  }
  return null;
}

function validatePhoneCode(account: Account): ErrorMsg | null {
  const phoneCode = account.phoneCode.trim();
  if (phoneCode.length < 1) {
    return { message: 'Please enter the phone country code' };
  }
  if (!phoneCode.match(RegEx.countryCode)) {
    return { message: 'Invalid phone country code' };
  }
  return null;
}

function validatePhoneNumber(account: Account): ErrorMsg | null {
  const phoneNumber = account.phoneNumber.trim();
  if (phoneNumber.length < 1) {
    return { message: 'Please enter the phone national number' };
  }
  if (!phoneNumber.match(RegEx.phone)) {
    return { message: 'Invalid phone number' };
  }
  return null;
}

function validateEmail(account: Account): ErrorMsg | null {
  const email = account.email.trim();
  if (email.length < 1) {
    return { message: 'Please enter the email' };
  }
  if (!email.match(RegEx.email)) {
    return { message: 'Invalid email address' };
  }
  return null;
}
